/**
 * mapCalculation.ts
 * Список функций для вычисления предопределенных значений.
 **/

import type {Extent} from "ol/extent";
import Point from "ol/geom/Point";
import {fromLonLat} from "ol/proj";
import {getCenter} from "ol/extent";
import {DISTRICT_CENTER_OVERRIDES, REGION_CENTER_OVERRIDES} from "@/components/modules/MainMap/utils/mapConstants";
import {FileData, ForecastParamData, ForecastRegionData} from "@/types/files";
import {VIEW_TYPE_IDS} from "@/composables/constants/map";

/**
 * Получает данные для округов и регионов: абсолютное и процент к АППГ
 */
export const getRegionValues = (
    fileData: FileData | ForecastRegionData,
    paramKey: string,
    isForecast: boolean
): { absoluteValue: number; percentValue: number } => {
    const paramData = fileData[paramKey];

    if (!paramData || typeof paramData !== "object") {
        return { absoluteValue: 0, percentValue: 0 };
    }

    if (isForecast) {
        const forecastParam = paramData as ForecastParamData;
        return {
            absoluteValue: forecastParam.prediction_value !== null ? Math.round(forecastParam.prediction_value) : 0,
            percentValue: forecastParam.percent_to_previous_year !== null ? forecastParam.percent_to_previous_year : 0
        };
    } else {
        const absoluteValue = paramData[VIEW_TYPE_IDS.ABSOLUTE as keyof typeof paramData];
        const percentValue = paramData["percent_to_previous_year" as keyof typeof paramData];

        return {
            absoluteValue: typeof absoluteValue === "number" ? absoluteValue : 0,
            percentValue: typeof percentValue === "number" ? percentValue : 0
        };
    }
};

/**
 * Определяет цвет региона/округа на основе процента к АППГ
 */
export const getRegionColor = (percentValue: number, isSquare: boolean): string => {
    const greenColor = "#49c966";
    const redColor = "#ff3834";
    const greenLightColor = "#92e3a8";
    const redLightColor = "#fc8c7c";

    if (isSquare) {
        return percentValue < 0 ? greenColor : redColor;
    } else {
        return percentValue < 0 ? greenLightColor : redLightColor;
    }
};


/**
 * Определяет минимальный zoom для показа данных на основе площади региона
 */
export const getMinZoomForCircle = (regionArea: number): number => {
    if (regionArea < 5e11) return 5;
    if (regionArea < 1e12) return 4;
    if (regionArea < 5e12) return 3;
    if (regionArea < 1e13) return 2;
    return 1;
};

/**
 * Получает центральную точку feature (с учетом переопределений)
 */
export const getCenterPoint = (
    featureKey: string,
    extent: Extent | null,
    isDistrict: boolean
): Point | null => {
    const overrideCoords = isDistrict
        ? DISTRICT_CENTER_OVERRIDES[featureKey]
        : REGION_CENTER_OVERRIDES[featureKey];

    if (overrideCoords) {
        return new Point(fromLonLat(overrideCoords));
    }

    if (extent) {
        const center = getCenter(extent);
        return new Point(center);
    }

    return null;
};